/** Step 1 — what the agent was asked to do (brief before grading). */

import { AgentToolbox } from "./AgentToolbox";
import { ChatThreadEvidence } from "./ChatThreadEvidence";
import { PhaseInstruction } from "./PhaseInstruction";
import type { AgentRun } from "./types";

interface IntentPhaseProps {
  run: AgentRun;
  intentConfirmed: boolean;
  intentFeedback: string;
  onConfirmChange: (confirmed: boolean) => void;
  onFeedbackChange: (value: string) => void;
}

export function IntentPhase({
  run,
  intentConfirmed,
  intentFeedback,
  onConfirmChange,
  onFeedbackChange,
}: IntentPhaseProps) {
  const isChat = run.run_kind === "chat";
  const intent = run.task_description?.trim();
  const doneWell = run.definition_of_done?.trim();
  const agentName = run.agent_name?.trim();
  const chatMessages = run.chat_messages ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PhaseInstruction>
        {isChat
          ? "Read this worker chat. When you understand what the user needed and what “done well” means, tick the box below and continue."
          : "Read what this agent was asked to do. When you understand the brief, tick the box below and continue to see what the agent actually did."}
      </PhaseInstruction>

      {agentName ? (
        <p className="text-sm text-slate-400">
          {isChat ? "Worker" : "Agent"}:{" "}
          <span className="text-slate-200 font-medium">{agentName}</span>
        </p>
      ) : null}

      {isChat && run.user_name?.trim() ? (
        <p className="text-sm text-slate-400">
          User: <span className="text-slate-200 font-medium">{run.user_name.trim()}</span>
        </p>
      ) : null}

      {run.subject_label && isChat ? (
        <h2 className="text-lg font-medium text-slate-100">{run.subject_label}</h2>
      ) : run.task_name ? (
        <h2 className="text-lg font-medium text-slate-100">{run.task_name}</h2>
      ) : null}

      {intent ? (
        <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{intent}</p>
      ) : null}

      {doneWell ? (
        <div className="rounded-xl bg-white/[0.03] px-4 py-3 ring-1 ring-white/5">
          <p className="text-xs font-medium text-emerald-300/90 mb-1">Done well looks like</p>
          <p className="text-sm text-slate-300 leading-relaxed">{doneWell}</p>
        </div>
      ) : null}

      {isChat && chatMessages.length > 0 ? (
        <div className="rounded-xl bg-white/[0.02] px-4 py-4 ring-1 ring-white/5">
          <p className="text-xs font-medium text-slate-300 mb-3">Full conversation</p>
          <ChatThreadEvidence messages={chatMessages} agentName={agentName || "Worker"} />
        </div>
      ) : null}

      <div className="rounded-xl bg-white/[0.02] px-4 py-4 ring-1 ring-white/5">
        <p className="text-xs font-medium text-slate-300 mb-3">
          {isChat ? "Worker toolbox" : "Agent toolbox"}
        </p>
        <AgentToolbox tools={run.available_tools} />
      </div>

      <div className="rounded-xl bg-white/[0.03] px-4 py-4 ring-1 ring-white/5 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={intentConfirmed}
            onChange={(e) => onConfirmChange(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500/40"
          />
          <span className="text-sm text-slate-200 leading-relaxed">
            {isChat
              ? "I understand what this chat was about and what “done well” means."
              : "I understand what this agent was asked to do and what “done well” means."}
          </span>
        </label>

        <div>
          <label htmlFor="intent-feedback" className="block text-xs text-slate-400 mb-1.5">
            {isChat
              ? "Anything unclear about this chat? (optional)"
              : "Anything unclear about this task? (optional — we'll use this to improve the brief)"}
          </label>
          <textarea
            id="intent-feedback"
            value={intentFeedback}
            onChange={(e) => onFeedbackChange(e.target.value)}
            placeholder={
              isChat
                ? "e.g. Hard to tell what the user ultimately wanted"
                : "e.g. I'm not sure what 'complete' means for goals"
            }
            rows={2}
            className="w-full rounded-xl bg-slate-900/50 px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 ring-1 ring-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
          />
        </div>
      </div>
    </div>
  );
}
